import React, { useMemo } from 'react'
import type { Column } from 'react-table'
import { useTranslation } from 'react-i18next'

import Actions from './componets/Actions'

import type { SortAccessors } from '@components/Table-v2'

import { BudgetFrequencyOption } from '../utils/constants'

import type { ListItem } from './types'
import { BudgetFrequency } from '@type/budget'
import { val2Amount } from '@helpers/utils'

/**
 * Sort values for the columns whose rendered text is not what you order by.
 *
 * Only `budget_frequency` needs one. `BudgetFrequencyOption` has no entry for
 * `custom`, so such a row RENDERS BLANK while its stored value is the word
 * "custom" — sorting on the record would file those rows under C, between
 * Monthly and Quarterly, with nothing in the cell to explain why. Reading the
 * same value the cell does sinks them with the other blanks.
 *
 * `budget_allocation_amount` deliberately has no entry: its column id is the
 * record field, so the default lookup already finds the raw minor-unit number
 * rather than the `val2Amount` string. Adding a redundant resolver here was
 * the first attempt, and its test passed with the resolver deleted.
 */
export const COST_CENTER_SORT_VALUES: SortAccessors<ListItem> = {
  budget_frequency: (record) => BudgetFrequencyOption[record.budget_frequency || BudgetFrequency.custom]?.label,
}

export const useGetTableColumns = () => {
  const { t } = useTranslation('accounting', { keyPrefix: 'costCenter.list.columns' })
  return useMemo<Column<ListItem>[]>(
    () => [
      {
        Header: t('nameLabel'),
        accessor: 'name',
      },
      {
        Header: t('codeLabel'),
        accessor: 'code',
      },
      {
        Header: t('owner'),
        accessor: 'owner',
      },
      {
        // Explicit id. With a function accessor and no id, react-table falls
        // back to the rendered Header — here a TRANSLATED string, which would
        // make the column's identity change with the user's language and
        // silently break any lookup keyed on it (COST_CENTER_SORT_VALUES).
        id: 'budget_frequency',
        Header: t('budgetFrequency'),
        accessor: (row) => {
          return BudgetFrequencyOption[row.budget_frequency || BudgetFrequency.custom]?.label
        },
      },
      {
        Header: t('currency'),
        accessor: 'currency',
      },
      {
        id: 'budget_allocation_amount',
        Header: t('currentBudget'),
        accessor: (record) => val2Amount(record.budget_allocation_amount || 0, { currency: record.currency }),
      },
      {
        id: 'actions',
        // Row buttons, not data: there is no order to put them in. Without
        // this react-table makes any column with an `accessor` sortable.
        disableSortBy: true,
        accessor: (record) => <Actions record={record} />,
      },
    ],
    [],
  )
}
